import './update-issuer.scss'

import { notification } from 'antd'
import axios from 'axios'
import { Formik, FormikHelpers } from 'formik'
import { Form, FormItem, Input, Select, SubmitButton, Switch } from 'formik-antd'
import React, { useEffect, useState } from 'react'
import { Column } from 'react-display-flex'
import useSWR from 'swr'

import { fetcher } from '../../../../api'
import { filterProps, parseApiErrorResponse } from '../../../../components/forms/utils'
import { convertEntityValuesToFormValues } from '../../../../util/convert-entity-values-to-form-values'
import { messages } from '../../messages'
import { Issuer, issuerFormInitialValues, IssuerFormValues } from '../../models'
import { issuerValidationSchema } from './validation'

interface Props {
  issuer?: Issuer
  onSuccess: () => void
}

export const UpdateIssuer = ({ issuer, onSuccess }: Props) => {
  const [formInitialValues, setFormInitialValues] = useState<IssuerFormValues>(issuerFormInitialValues)

  const { data: bankingGroups } = useSWR<Issuer[]>('/banking-groups', fetcher)

  useEffect(() => {
    if (!issuer) return

    const formValues = convertEntityValuesToFormValues(issuerFormInitialValues, issuer)
    formValues.frontOfficeEmail = splitEmails(issuer.frontOfficeEmail)
    formValues.backOfficeEmail = splitEmails(issuer.backOfficeEmail)

    setFormInitialValues(formValues)
  }, [issuer])

  const onSubmit = async (
    issuerFormValues: IssuerFormValues,
    { setSubmitting, resetForm, setErrors }: FormikHelpers<IssuerFormValues>,
  ) => {
    const issuerPayloadValues = convertFormValuesToIssuerRequestPayload(issuerFormValues)

    try {
      await axios.put(`/issuers/${issuerFormValues.id}`, issuerPayloadValues)

      notification.success({ message: messages.common.success, description: messages.update.success })
      onSuccess()
      resetForm()
    } catch (error) {
      const formErrors = parseApiErrorResponse(error)
      setErrors(formErrors)
      setSubmitting(false)
      notification.error({ message: messages.common.error, description: messages.update.error })
    }
  }

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={issuerValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <Form name="update-issuer">
        <Input name="id" hidden />
        <FormItem name="shortName" label="Display Name" required>
          <Input name="shortName" placeholder="Issuer name" />
        </FormItem>
        <FormItem name="name" label="Long Name" required>
          <Input name="name" placeholder="Issuer descriptive name" />
        </FormItem>
        <FormItem name="code" label="Code" required>
          <Input name="code" placeholder="Code" />
        </FormItem>
        <FormItem name="bankingGroupId" label="Banking Group" required>
          <Select
            name="bankingGroupId"
            placeholder="Select the banking group"
            showSearch
            loading={!bankingGroups}
            options={bankingGroups?.map(({ id, name, code }) => ({ label: `${name} (${code})`, value: id }))}
            {...filterProps}
          />
        </FormItem>
        <FormItem name="backOfficeEmail" label="Back Office Email" required>
          <Select name="backOfficeEmail" placeholder="Back Office Email" mode="tags" allowClear />
        </FormItem>
        <FormItem name="frontOfficeEmail" label="Front Office Email" required>
          <Select name="frontOfficeEmail" placeholder="Front Office Email" mode="tags" allowClear />
        </FormItem>
        <FormItem name="leiCode" label="LEI Code">
          <Input name="leiCode" placeholder="Lei Code" />
        </FormItem>
        <FormItem name="clearCode" label="Clear Code">
          <Input name="clearCode" placeholder="Clear Code" />
        </FormItem>
        <FormItem name="fundsFossilFuels" label="Funds Fossil Fuels">
          <Switch name="fundsFossilFuels" checkedChildren="Yes" unCheckedChildren="No" />
        </FormItem>
        <Column>
          <SubmitButton htmlType="submit" type="primary">
            Update Issuer
          </SubmitButton>
        </Column>
      </Form>
    </Formik>
  )
}

const splitEmails = (concatenatedEmails: string): string[] =>
  concatenatedEmails ? concatenatedEmails.split(',').map((email) => email.trim()) : []

const convertFormValuesToIssuerRequestPayload = (values: IssuerFormValues) => ({
  ...values,
  backOfficeEmail: values.backOfficeEmail.join(','),
  frontOfficeEmail: values.frontOfficeEmail.join(','),
})
